/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { BaseApiService } from './BaseApiService';
import type { GenericAbortSignal, RawAxiosRequestHeaders } from 'axios';
import type {
  VouchersRequest,
  VouchersResponse,
  SmartFillRequest,
  SmartFillResponse,
  HotelDetailResponse,
  HotelCheckoutRequest,
  HotelCheckoutResponse,
  HotelPreCheckoutRequest,
  HotelPreCheckoutResponse,
  GetHotelRoomsParams,
  RoomsResponse,
  GuestReviewsApiResponse,
  HotelBookingConfirmationResponse,
  HotelBookingPrecancellationResponse,
  HotelCancelBookingRequest,
  HotelCancelBookingResponse,
  HotelEmailVoucherRequest,
  HotelEmailVoucherResponse,
  HotelBookingRoomContentResponse,
} from '../types';
import { AppEnvironment } from '../types';

const BOOKING_WRAPPER_END_USER = 'test-airasia';

const getBookingWrapperHeaders = (
  clientId: string,
  hotelToken: string,
): RawAxiosRequestHeaders => ({
  'client-id': clientId,
  ...(hotelToken ? { Authorization: `Bearer ${hotelToken}` } : {}),
  'Content-Type': 'application/json',
});

export class HotelsApiService extends BaseApiService {
  constructor(environment: AppEnvironment = 'stg') {
    super(environment, {
      stg: 'https://hotelsbe.stg-apiairasia.com',
      prd: 'https://hotelsbe.apiairasia.com',
    });
  }

  public getVouchers(
    request: VouchersRequest,
    hotelToken: string,
    clientId: string,
  ): Promise<VouchersResponse> {
    return this.get(
      '/bff/discounts/v2/promos',
      {
        currencyCode: request.currencyCode.toUpperCase(),
        email: request.email,
      },
      {
        'client-id': clientId,
        Authorization: `Bearer ${hotelToken}`,
      },
    );
  }
  public getSmartFillSuggestions(
    request: SmartFillRequest,
    hotelToken: string,
    clientId: string,
  ): Promise<SmartFillResponse> {
    return this.get(
      '/smartfill/v3/smartfill',
      {
        country_code: request.countryCode,
        query: request.query,
        language: request.language,
      },
      {
        'client-id': clientId,
        Authorization: `Bearer ${hotelToken}`,
      },
    );
  }

  public getHotelDetail(
    hotelId: string,
    language: string,
    hotelToken: string,
    clientId: string,
    lemonAgentId?: string,
  ): Promise<HotelDetailResponse> {
    const params: Record<string, string> = { language };
    if (lemonAgentId) {
      params.lemon_agent_id = lemonAgentId;
    }
    return this.get(`/details/v2/hotels/${hotelId}/content`, params, {
      'client-id': clientId,
      Authorization: `Bearer ${hotelToken}`,
    });
  }

  public getHotelRooms(
    params: GetHotelRoomsParams,
    hotelToken: string,
    clientId: string,
    accessToken?: string,
    refreshToken?: string,
    signal?: GenericAbortSignal,
  ): Promise<RoomsResponse> {
    const {
      hotelId,
      currency,
      countryCode,
      checkin,
      checkout,
      occupancy,
      language,
      debug,
      channel,
      mft,
      asProfile,
    } = params;

    // Build query params object
    const queryParams: Record<string, string | string[]> = {
      ...(currency && { currency }),
      ...(countryCode && { country_code: countryCode }),
      ...(checkin && { checkin }),
      ...(checkout && { checkout }),
      ...(language && { language }),
      ...(typeof debug === 'boolean' && { debug: String(debug) }),
      ...(channel && { channel }),
      ...(mft && { mft }),
    };

    // Handle occupancy - can be array for multi-room or single number
    if (occupancy) {
      if (Array.isArray(occupancy)) {
        // For multi-room: pass as array to create multiple occupancy params
        // Example: occupancy=2(0)&occupancy=2(1_0)
        queryParams.occupancy = occupancy;
      } else {
        // For single room: convert number to string format
        queryParams.occupancy = `${occupancy}(0)`;
      }
    }

    return this.get(
      `/bff/details/v2/hotels/${hotelId}/rooms`,
      queryParams,
      {
        'client-id': clientId,
        ...(hotelToken ? { Authorization: `Bearer ${hotelToken}` } : {}),
        'Content-Type': 'application/json',
        ...(accessToken && { 'X-Auth': accessToken }),
        ...(refreshToken && { 'refresh-token': refreshToken }),
        'X-AS-Profile': asProfile ?? '0',
      },
      signal ? { signal } : undefined,
    );
  }

  public getGuestReviews(
    hotelId: string,
    language: string,
    hotelToken: string,
    clientId: string,
  ): Promise<GuestReviewsApiResponse> {
    return this.get(
      '/review/v1/hotels/reviews',
      {
        hotel_id: hotelId,
        language,
      },
      {
        'client-id': clientId,
        Authorization: `Bearer ${hotelToken}`,
      },
    );
  }

  public preCheckout(
    request: HotelPreCheckoutRequest,
    hotelToken: string,
    clientId: string,
  ): Promise<HotelPreCheckoutResponse> {
    return this.post(
      `/hotel-checkout/v2/hotels/${encodeURIComponent(
        request.hotelId,
      )}/rooms/${encodeURIComponent(request.roomId)}/pre_checkout`,
      {
        token: request.token,
        language: request.language,
      },
      undefined,
      {
        'client-id': clientId,
        ...(hotelToken && { Authorization: `Bearer ${hotelToken}` }),
        'Content-Type': 'application/json',
      },
    );
  }

  public checkout(
    request: HotelCheckoutRequest,
    hotelToken: string,
    clientId: string,
  ): Promise<HotelCheckoutResponse> {
    const {
      filter: hotelFilter,
      fetchApi: _fetchApi,
      ...restRequest
    } = request;
    const occupancy = Array.isArray(hotelFilter.occupancy)
      ? hotelFilter.occupancy.map((guest) => guest.toString())
      : [];

    return this.post(
      '/hotel-checkout/v1/checkout',
      {
        ...restRequest,
        checkin_date: hotelFilter.checkin,
        checkout_date: hotelFilter.checkout,
        occupancy_per_room: occupancy,
        user_locale: hotelFilter.languageId || hotelFilter.language || 'en-gb',
      },
      undefined,
      {
        'client-id': clientId,
        ...(hotelToken ? { Authorization: `Bearer ${hotelToken}` } : {}),
        'Content-Type': 'application/json',
      },
    );
  }

  public getBookingConfirmation(
    key: string,
    hotelToken: string,
    clientId: string,
    currency?: string,
  ): Promise<HotelBookingConfirmationResponse> {
    return this.get(
      `/booking/v1/hotels/booking/wrapper/order/${key}`,
      currency ? { currency: currency.toUpperCase() } : undefined,
      {
        'client-id': clientId,
        ...(hotelToken ? { Authorization: `Bearer ${hotelToken}` } : {}),
        'Content-Type': 'application/json',
      },
    );
  }

  public getBookingPrecancellation(
    preCancelHref: string,
    hotelToken: string,
    clientId: string,
  ): Promise<HotelBookingPrecancellationResponse> {
    const url = preCancelHref.startsWith('/booking')
      ? preCancelHref
      : `/booking${preCancelHref}`;

    return this.get(
      url,
      undefined,
      getBookingWrapperHeaders(clientId, hotelToken),
    );
  }

  public cancelBooking(
    request: HotelCancelBookingRequest,
    hotelToken: string,
    clientId: string,
  ): Promise<HotelCancelBookingResponse> {
    const url = request.cancelHref.startsWith('/booking')
      ? request.cancelHref
      : `/booking${request.cancelHref}`;

    return this.deleteWithData(
      url,
      {
        reason: request.reason,
        refund: request.refund,
      },
      getBookingWrapperHeaders(clientId, hotelToken),
    );
  }

  public sendHotelVoucherEmail(
    request: HotelEmailVoucherRequest,
    hotelToken: string,
    clientId: string,
  ): Promise<HotelEmailVoucherResponse> {
    const url = request.voucherHref.startsWith('/booking')
      ? request.voucherHref
      : `/booking${request.voucherHref}`;

    return this.post(
      url,
      { email: request.email },
      undefined,
      getBookingWrapperHeaders(clientId, hotelToken),
    );
  }

  public getBookingRoomContent(
    hotelId: string,
    roomId: string,
    language: string,
    clientId: string,
  ): Promise<HotelBookingRoomContentResponse> {
    return this.get(
      `/details/v2/hotels/${encodeURIComponent(
        hotelId,
      )}/room/${encodeURIComponent(roomId)}/content`,
      { language },
      {
        'client-id': clientId,
        'Content-Type': 'application/json',
      },
    );
  }

  public getBookingHotelContent(
    hotelId: string,
    language: string,
    clientId: string,
  ): Promise<HotelDetailResponse> {
    return this.get(
      `/details/v2/hotels/${encodeURIComponent(hotelId)}/content`,
      { language },
      {
        'client-id': clientId,
        'Content-Type': 'application/json',
      },
    );
  }
}
